import type { CSSProperties, ReactNode, FC, PropsWithChildren } from 'react'
import { createContext, useContext, useMemo } from 'react'
import { App as AntdApp, message } from 'antd'
import { InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons'

type Kind = 'success' | 'info' | 'error' | 'warning'
type NamedColor = 'success' | 'error' | 'warning' | 'info'
type CssVarName =
  | '--cor-sucesso-trasparente'
  | '--cor-perigo-trasparente'
  | '--cor-aviso-trasparente'
  | '--cor-info-trasparente'

type CustomColor = NamedColor | { cssVar: CssVarName }

interface ShowOptions {
  content?: ReactNode
  duration?: number
}

interface CustomOptions {
  message: ReactNode
  duration?: number
  icon?: ReactNode
  color?: CustomColor
}

interface LoadingStartOptions {
  content?: ReactNode
  icon?: ReactNode
}

interface MessageContextValue {
  show: (kind: Kind, options?: ShowOptions) => void

  success(content: ReactNode, duration?: number): void
  success(options?: ShowOptions): void

  info(content: ReactNode, duration?: number): void
  info(options?: ShowOptions): void

  error(content: ReactNode, duration?: number): void
  error(options?: ShowOptions): void

  warning(content: ReactNode, duration?: number): void
  warning(options?: ShowOptions): void

  loading(content: ReactNode, duration?: number): void
  loading(options?: ShowOptions): void

  custom: (opts: CustomOptions) => void
  startLoading: (opts?: LoadingStartOptions) => void
  stopLoading: () => void
}

const DEFAULT_TEXT: Record<Kind, string> = {
  success: 'Operation completed successfully.',
  info: 'Here is some information.',
  error: 'Something went wrong.',
  warning: 'Please pay attention to this.',
}

const DEFAULT_DURATION: Record<Kind, number> = {
  success: 3,
  info: 4,
  warning: 5,
  error: 6.5,
}

const DEFAULT_LOADING_TEXT = 'Loading...'
const DEFAULT_LOADING_DURATION = 0

const GLOBAL_MSG_KEY = 'app-message'
const MessageContext = createContext<MessageContextValue | null>(null)

const colorToStyle = (color?: CustomColor): CSSProperties | undefined => {
  if (!color) return undefined
  if (color === 'success') return { background: 'var(--cor-sucesso-trasparente)' }
  if (color === 'error') return { background: 'var(--cor-perigo-trasparente)' }
  if (color === 'warning') return { background: 'var(--cor-aviso-trasparente)' }
  if (color === 'info') return { background: 'var(--cor-info-trasparente)' }
  if (typeof color === 'object' && 'cssVar' in color) return { background: `var(${color.cssVar})` }
  return undefined
}

function parseArgsWithDefaults(
  fallbackContent: ReactNode,
  fallbackDuration: number,
  args: [ReactNode, number?] | [ShowOptions?] | []
): { content: ReactNode; duration: number } {
  const first = args[0]
  if (first === undefined) {
    return { content: fallbackContent, duration: fallbackDuration }
  }
  if (typeof first !== 'object' || 'props' in (first as any)) {
    const content = first as ReactNode
    const duration = args[1] ?? fallbackDuration
    return { content, duration }
  }
  const opts = first as ShowOptions
  return {
    content: opts.content ?? fallbackContent,
    duration: opts.duration ?? fallbackDuration,
  }
}

export const MessageProvider: FC<PropsWithChildren> = ({ children }) => {
  const [messageApi, contextHolder] = message.useMessage({ maxCount: 1 })

  const show = (kind: Kind, opts?: ShowOptions) => {
    const { content, duration } = parseArgsWithDefaults(DEFAULT_TEXT[kind], DEFAULT_DURATION[kind], [opts ?? {}])
    void messageApi.open({
      key: GLOBAL_MSG_KEY,
      type: kind,
      content,
      duration,
    })
  }

  const showOverloaded =
    (kind: Kind) =>
    (...args: any[]) => {
      const { content, duration } = parseArgsWithDefaults(DEFAULT_TEXT[kind], DEFAULT_DURATION[kind], args as any)
      void messageApi.open({
        key: GLOBAL_MSG_KEY,
        type: kind,
        content,
        duration,
      })
    }

  const loadingOverloaded =
    () =>
    (...args: any[]) => {
      const { content, duration } = parseArgsWithDefaults(DEFAULT_LOADING_TEXT, DEFAULT_LOADING_DURATION, args as any)
      void messageApi.open({
        key: GLOBAL_MSG_KEY,
        type: 'loading',
        content,
        duration,
        icon: <LoadingOutlined />,
      })
    }

  const custom = (opts: CustomOptions) => {
    const { message: content, duration = 3, icon, color } = opts
    void messageApi.open({
      key: GLOBAL_MSG_KEY,
      content,
      duration,
      icon: icon ?? <InfoCircleOutlined />,
      style: colorToStyle(color),
    })
  }

  const startLoading = (opts?: LoadingStartOptions) => {
    void messageApi.open({
      key: GLOBAL_MSG_KEY,
      type: 'loading',
      content: opts?.content ?? DEFAULT_LOADING_TEXT,
      duration: 0,
      icon: opts?.icon ?? <LoadingOutlined />,
    })
  }

  const stopLoading = () => {
    messageApi.destroy(GLOBAL_MSG_KEY)
  }

  const value = useMemo<MessageContextValue>(
    () => ({
      show,
      success: showOverloaded('success') as any,
      info: showOverloaded('info') as any,
      error: showOverloaded('error') as any,
      warning: showOverloaded('warning') as any,
      loading: loadingOverloaded() as any,
      custom,
      startLoading,
      stopLoading,
    }),
    []
  )

  return (
    <MessageContext value={value}>
      {contextHolder}
      <AntdApp>{children}</AntdApp>
    </MessageContext>
  )
}

export const useAppMessage = (): MessageContextValue => {
  const ctx = useContext(MessageContext)
  if (!ctx) throw new Error('useAppMessage must be used within <MessageProvider>')
  return ctx
}
